import React, { useState, useRef } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import CloseIcon from '@mui/icons-material/Close';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  chip: {
    backgroundColor: '#F0FFF4',
    border: '1px solid #9AE6B4',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#E6FFED',
    },
    '& .MuiChip-label': {
      color: '#276749',
      fontWeight: 500,
    },
  },
  chipEmpty: {
    backgroundColor: '#F7FAFC',
    border: '1px solid #E2E8F0',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#EDF2F7',
    },
    '& .MuiChip-label': {
      color: '#718096',
      fontWeight: 500,
    },
  },
  editableChip: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1.5),
    backgroundColor: '#F0FFF4',
    border: '1px solid #38A169',
    borderRadius: 16,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  teamSelect: {
    minWidth: 220,
    '& .MuiInputBase-root': {
      fontSize: 13,
      backgroundColor: '#fff',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#CBD5E0',
    },
  },
  closeButton: {
    padding: 2,
    color: '#4A5568',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.05)',
    },
  },
  closeIcon: {
    fontSize: 16,
  },
  dropdownOption: {
    padding: theme.spacing(0.75, 1.5),
    margin: theme.spacing(0.25, 0.5),
    borderRadius: 4,
    fontSize: 13,
    '&:hover': {
      backgroundColor: '#EDF2F7',
    },
    '&[aria-selected="true"]': {
      backgroundColor: '#F0FFF4',
    },
  },
  dropdownPaper: {
    marginTop: 4,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    border: '1px solid #E2E8F0',
    borderRadius: 6,
  },
  dropdownListbox: {
    padding: theme.spacing(0.5, 0),
  },
  selectedChip: {
    marginRight: 2,
    marginBottom: 2,
    height: 22,
    fontSize: 12,
  },
}));

interface EditableGithubTeamSelectorProps {
  selectedTeams: string[];
  availableTeams: string[];
  onSave: (teams: string[]) => void;
  onCancel: () => void;
}

const EditableGithubTeamSelector: React.FC<EditableGithubTeamSelectorProps> = ({
  selectedTeams,
  availableTeams,
  onSave,
  onCancel,
}) => {
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const [localSelected, setLocalSelected] = useState<string[]>(selectedTeams);

  const handleChange = (newValue: string[]) => {
    setLocalSelected(newValue);
  };

  const handleClickAway = () => {
    onSave(localSelected);
  };

  const sortedOptions = [...availableTeams].sort((a, b) => a.localeCompare(b));

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Paper className={classes.editableChip} ref={containerRef} elevation={0}>
        <Autocomplete
          multiple
          size="small"
          className={classes.teamSelect}
          options={sortedOptions}
          value={localSelected}
          onChange={(_, newValue) => handleChange(newValue)}
          disableCloseOnSelect
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={localSelected.length === 0 ? "All teams (no filter)" : ""}
              variant="outlined"
              size="small"
            />
          )}
          getOptionLabel={(option) => option}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option}
                label={option}
                size="small"
                className={classes.selectedChip}
              />
            ))
          }
          renderOption={(props, option, { selected }) => (
            <li {...props} className={classes.dropdownOption}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option}
            </li>
          )}
          slotProps={{
            paper: { className: classes.dropdownPaper },
          }}
          ListboxProps={{ className: classes.dropdownListbox }}
          openOnFocus
          autoHighlight
        />
        <IconButton
          className={classes.closeButton}
          size="small"
          onClick={onCancel}
        >
          <CloseIcon className={classes.closeIcon} />
        </IconButton>
      </Paper>
    </ClickAwayListener>
  );
};

interface GithubTeamFilterProps {
  selectedTeams: string[];
  availableTeams: string[];
  onSelectionChange: (teams: string[]) => void;
}

export const GithubTeamFilter: React.FC<GithubTeamFilterProps> = ({
  selectedTeams,
  availableTeams,
  onSelectionChange,
}) => {
  const classes = useStyles();
  const [isEditing, setIsEditing] = useState(false);

  const formatChipLabel = (): string => {
    if (selectedTeams.length === 0) {
      return 'GitHub Team = All';
    }
    if (selectedTeams.length === 1) {
      return `GitHub Team = ${selectedTeams[0]}`;
    }
    return `GitHub Team = (${selectedTeams.length})`;
  };

  const handleChipClick = () => {
    setIsEditing(true);
  };

  const handleSaveTeams = (teams: string[]) => {
    onSelectionChange(teams);
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Box className={classes.container}>
        <EditableGithubTeamSelector
          selectedTeams={selectedTeams}
          availableTeams={availableTeams}
          onSave={handleSaveTeams}
          onCancel={handleCancelEdit}
        />
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Tooltip
        title={
          selectedTeams.length === 0
            ? 'Click to filter by GitHub team'
            : selectedTeams.length > 1
            ? selectedTeams.join(', ')
            : 'Click to edit filter'
        }
      >
        <Chip
          className={selectedTeams.length === 0 ? classes.chipEmpty : classes.chip}
          label={formatChipLabel()}
          onClick={handleChipClick}
          size="small"
        />
      </Tooltip>
    </Box>
  );
};

export default GithubTeamFilter;
